import { Location } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HTTPStatusCodes, skipDefaultResponseHandler } from '@asamco/utils';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import swal from 'sweetalert2';

import { AsamcoAuthService } from '../auth/asamco-auth.service';
import { AppSettings } from '../configuration/appsettings';

/**
 * Interceptor that handles some standard error responses
 */
@Injectable({
	providedIn: 'root',
	deps: [Router, AppSettings, Location, AsamcoAuthService],
})
export class ErrorHandlingInterceptorService implements HttpInterceptor {
	/** Track login state */
	private isLoggedIn = false;

	constructor(private router: Router, private app: AppSettings, private loc: Location, private authService: AsamcoAuthService) {
		this.authService.isLoggedIn$
			.pipe(
				tap(newState => {
					this.isLoggedIn = newState;
				})
			)
			.subscribe({error: console.error});
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (request.headers.has(skipDefaultResponseHandler)) {
			const headers = request.headers.delete(skipDefaultResponseHandler);
			request = request.clone({ headers });
			return next.handle(request);
		}

		return next.handle(request).pipe(
			catchError((err: HttpErrorResponse) => {
				switch (err.status) {
					/** 401 unauthorized */
					case HTTPStatusCodes.UNAUTHORIZED:
						return this.handleUnauthorized(err);
					/** 402, license issue */
					case HTTPStatusCodes.PAYMENT_REQUIRED:
						return this.handleLicenseIssue(err);
					/** 403, license issue */
					case HTTPStatusCodes.FORBIDDEN:
						return this.handlePermissionIssue(err);
					default:
						return throwError(err);
				}
			})
		);
	}

	handleUnauthorized(err: HttpErrorResponse): Observable<any> {
		this.app.addOrUpdateSetting('lastRouteBeforeLogin', this.router.routerState.snapshot.url);

		if (err.error && err.error.message && err.error.message === 'Authorization has been denied for this request.') {
			/** HACK to cater for non-existing 403 */
			swal.fire({
				icon: 'error',
				title: 'Permission not granted by server',
				text: 'Contact your administrator.',
			});
			// and navigate to 403.
			this.router.navigate(['/error/no-access']);
			return EMPTY;
		}

		// 401, uses has an invalid session somehow.
		if (this.loc.path().includes('/auth/login') || this.loc.path().includes('/auth/logoff')) {
			/** no need to redirect to login if already there. */
			return EMPTY;
		}

		return from(this.authService.logOff()).pipe(
			tap(_ => {
				swal.fire({
					icon: 'error',
					title: 'User not authenticated',
					text: (this.isLoggedIn ? 'Your session has expired. ' : '') + 'Please login first.',
				});
			}),
			concatMap(_ => EMPTY)
		);
	}

	handleLicenseIssue(err: HttpErrorResponse): Observable<any> {
		this.app.addOrUpdateSetting('lastRouteBeforeLogin', this.router.routerState.snapshot.url);

		if (err.error === 'License invalid') {
			swal.fire({
				icon: 'error',
				title: 'License invalid',
				text: 'Please validate your license first',
			});
			// navigate to license validate page.
			this.router.navigate(['/license/validate']);
		} else {
			swal.fire({
				icon: 'error',
				title: 'too many users, expand your licence',
				text: 'Contact your administrator.',
			});
			// and navigate to 402.
			this.router.navigate(['/error/no-license']);
		}

		return EMPTY;
	}

	handlePermissionIssue(err: HttpErrorResponse): Observable<any> {
		this.app.addOrUpdateSetting('lastRouteBeforeLogin', this.router.routerState.snapshot.url);

		swal.fire({
			icon: 'error',
			title: 'Permission not granted by server',
			text: 'Contact your administrator.',
		});

		return EMPTY;
	}
}
